import React, { FC } from "react";
import Logo from "@/shared/Logo";
import MenuBar from "@/shared/MenuBar";
import LangDropdown from "./LangDropdown";
import CartDropdown from "./CartDropdown";
import CartTrigger from "./CartTrigger";
import AvatarDropdown from "./AvatarDropdown";
import HeroSearchForm2MobileFactory from "../(HeroSearchForm2Mobile)/HeroSearchForm2MobileFactory";
import Link from "next/link";
import { Route } from "@/routers/types";
import { useAppSelector } from '@/app/lib/hooks';
import { RootState } from '@/app/lib/store';
import { useTranslation } from 'react-i18next';
// 2026 header redesign — the desktop nav uses these three megamenus instead
// of the legacy MenuDropdown / WorkWithUs / inline tour & contact links.
// Mobile section below stays unchanged.
import DestinationsMegaMenu from "./DestinationsMegaMenu";
import ServicesMegaMenu from "./ServicesMegaMenu";
import WorkWithUsRedesign from "./WorkWithUsRedesign";
import SearchTrigger from "./SearchTrigger";

export interface MainNav2Props {
  className?: string;
}

const MainNav2: FC<MainNav2Props> = ({ className = "" }) => {
  const { t } = useTranslation("guydeez");
  const cu = useAppSelector((state: RootState) => state.auth.user);

  return (
    <div className={`MainNav2 relative z-10 ${className}`}>
      {/* Full-width topbar: drop the lg:container max-width and use side
          padding (matches the 2026 redesign artboard's 56 px gutter). */}
      <div className="px-4 lg:px-14 h-20 w-full flex justify-between">
        {/* ── Desktop left: logo + 4 main nav entries (redesign) ── */}
        <div className="hidden lg:flex justify-start flex-1 space-x-3 sm:space-x-8 lg:space-x-10">
          <Logo className="w-24 self-center" />
          <div className="hidden lg:block self-center h-10 border-l border-white/30"></div>
          <div className="hidden lg:flex items-center gap-1">
            <DestinationsMegaMenu />
            <ServicesMegaMenu />
            <WorkWithUsRedesign />
            <Link
              href={"/contact" as Route<string>}
              className="self-center text-sm font-medium text-white/90 hover:text-white px-2 py-2"
            >
              {t('contact_us')}
            </Link>
          </div>
        </div>

        <div className="self-center lg:hidden flex-[3] max-w-lg !mx-auto md:px-3">
          <HeroSearchForm2MobileFactory />
        </div>

        {/* ── Desktop right: search, cart, lang, login ── */}
        <div className="hidden md:flex flex-shrink-0 justify-end flex-1 lg:flex-none text-white">
          {/* SearchTrigger and CartDropdown render their trigger buttons
              with no inherent color; they pick up the parent `text-white`
              naturally. LangDropdown's trigger was forcing its own dark
              color — that's been changed to inherit in LangDropdown.tsx so
              we don't need any cascading override here. */}
          <div className="hidden lg:flex items-center gap-1">
            <SearchTrigger />
            <CartTrigger />
            <LangDropdown />

            {cu ?
            <AvatarDropdown />
            :
            <Link
              href={"/login" as Route<string>}
              className="self-center group ml-2 px-5 py-2 border border-white/60 hover:bg-white hover:text-[#2E8C4D] rounded-full inline-flex items-center text-sm text-white font-medium transition-colors"
            >
              {t('login')}
            </Link>
            }
          </div>
          <div className="flex space-x-2 sm:hidden">
            {/*<NotifyDropdown />*/}
            <CartDropdown />
            {cu ?
            <AvatarDropdown />
            :
            <Link
              href={"/login" as Route<string>}
              className="self-center text-opacity-90 group px-4 py-2 border border-neutral-300 hover:border-neutral-400 dark:border-neutral-700 rounded-full inline-flex items-center text-sm text-gray-700 dark:text-neutral-300 font-medium hover:text-opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75"
            >
              {t('login')}
            </Link>
            }
            <MenuBar />
          </div>
        </div>
      </div>
    </div>
  );
};

export default MainNav2;
